// src/services/contentService.js
// Priority: MySQL (latest) → default (section ที่ยังไม่มีใน MySQL)
//
// Redesign (2026-10) uses NEW section keys from site-content-v2.json (siteHeader, homeHero, …).
// Old sections (navbar, home, about, packages, contact, footer) stay in the DB untouched for the
// old production build; they are carried through on save but are no longer defaults here.

import mockupRaw from './site-content-v2.json';

const CACHE_KEY = 'site_content_cache_v2';

if (typeof localStorage !== 'undefined') {
  localStorage.removeItem('site_content_v1');
}

// strip _meta / _guide keys จาก mockup
function cleanMockup(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('_')) continue;
    out[k] = typeof v === 'object' && !Array.isArray(v) ? cleanMockup(v) : v;
  }
  return out;
}

export const DEFAULT_SITE_CONTENT = cleanMockup(mockupRaw);

// ── mergeSiteContent ──────────────────────────────────────────────────────────
// Merge ทีละ section:
//   - section ที่มีใน MySQL → ใช้ข้อมูล MySQL โดยเติม field ที่ยังไม่มี (field ใหม่ที่เพิ่มภายหลัง) จาก default
//   - section ที่ยังไม่มีใน MySQL → ใช้ mockup default
// ทำให้แต่ละ component แสดง MySQL เมื่อมีข้อมูล และ mockup เมื่อยังไม่มี
function mergeSiteContent(dbContent, defaults) {
  const merged = {};

  // วน section ทั้งหมดจาก defaults (schema ครบ)
  Object.keys(defaults).forEach((section) => {
    const dbSection = dbContent?.[section];
    const hasDbSection =
      dbSection !== null &&
      dbSection !== undefined &&
      typeof dbSection === 'object' &&
      !Array.isArray(dbSection) &&
      Object.keys(dbSection).length > 0;

    const def = defaults[section];
    const defIsObject = def && typeof def === 'object' && !Array.isArray(def);
    merged[section] = hasDbSection ? (defIsObject ? { ...def, ...dbSection } : dbSection) : def;
  });

  // รวม section พิเศษที่มีใน DB แต่ไม่มีใน defaults (future-proof)
  if (dbContent) {
    Object.keys(dbContent).forEach((section) => {
      if (!(section in merged)) merged[section] = dbContent[section];
    });
  }

  return merged;
}

// ── cache helpers ─────────────────────────────────────────────────────────────
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(content) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(content)); } catch { /* quota */ }
}

// ── getSiteContent ────────────────────────────────────────────────────────────
// 1. ดึงจาก MySQL ก่อนเสมอ (ข้อมูลล่าสุด)
// 2. Merge กับ DEFAULT ทีละ section (section ที่ MySQL ยังไม่มี → ใช้ mockup)
// 3. Network error → ใช้ cache (merged กับ default) → DEFAULT ทั้งหมด
export async function getSiteContent() {
  try {
    const res  = await fetch('/api/site-content');
    const data = await res.json().catch(() => ({}));

    if (data.success) {
      // มีข้อมูลใน MySQL → merge กับ default ทีละ section
      const merged = mergeSiteContent(data.content || {}, DEFAULT_SITE_CONTENT);
      writeCache(merged);
      return merged;
    }

    // API ตอบแต่ไม่ success → network ok แต่ server มีปัญหา → ใช้ cache
    const cached = readCache();
    return cached ? mergeSiteContent(cached, DEFAULT_SITE_CONTENT) : DEFAULT_SITE_CONTENT;
  } catch (err) {
    // Network error → ใช้ cache, fallback DEFAULT
    console.warn('[contentService] getSiteContent network error:', err.message);
    const cached = readCache();
    return cached ? mergeSiteContent(cached, DEFAULT_SITE_CONTENT) : DEFAULT_SITE_CONTENT;
  }
}

// ── loadSiteContentFromServer ─────────────────────────────────────────────────
// For the admin editor: the server copy or an error — never the cache or defaults,
// so the editor can never show (and then save) stand-in content as if it were real.
export async function loadSiteContentFromServer() {
  const res = await fetch('/api/site-content', { cache: 'no-store' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
  const merged = mergeSiteContent(data.content || {}, DEFAULT_SITE_CONTENT);
  writeCache(merged);
  return merged;
}

// ── saveSiteContent ───────────────────────────────────────────────────────────
export async function saveSiteContent(nextContent) {
  const res  = await fetch('/api/site-content', {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(nextContent),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    throw new Error(data.message || `Save failed: HTTP ${res.status}`);
  }

  // Merge ผลลัพธ์จาก server กับ default ก่อน cache
  const merged = mergeSiteContent(data.content || {}, DEFAULT_SITE_CONTENT);
  writeCache(merged);
  return merged;
}

// ── resetSiteContent ──────────────────────────────────────────────────────────
export function resetSiteContent() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  return DEFAULT_SITE_CONTENT;
}
